"use client";

import { CheckoutForm } from "@/checkout/types";
import { Card, Typography } from "@cappelendamm/design-system/react";
import {
  formatPrice,
  resolveFirstOrderPrice,
  SubscriptionProduct,
} from "@cappelendamm/e-commerce-services";
import type { BookClubItem } from "@cappelendamm/catalogue/crystallize/items/get-all-bookclubs.js";
import Image from "next/image";
import { Check } from "iconoir-react";
import { useFormContext, useWatch } from "react-hook-form";
import { getSelectedGiftsFromForm } from "@/checkout/subscription/gift-selection";
import { formatCountLabel } from "@/utils/formatCountLabel";

type BookClubCheckoutSummaryProps = {
  subscription: SubscriptionProduct;
  subscriptionImage?: BookClubItem["image"];
};

const getSummaryContentLabel = (selectedGifts: { isBook?: boolean }[]) => {
  const selectedBookCount = selectedGifts.filter((gift) => gift.isBook).length;
  const selectedGiftCount = Math.max(
    selectedGifts.length - selectedBookCount,
    0,
  );

  const labelParts: string[] = [];
  if (selectedGiftCount > 0) {
    labelParts.push(formatCountLabel(selectedGiftCount, "gave", "gaver"));
  }
  if (selectedBookCount > 0) {
    labelParts.push(formatCountLabel(selectedBookCount, "bok", "bøker"));
  }

  if (labelParts.length === 0) {
    return "Velkomstgave";
  }

  return `Velkomstgave: ${labelParts.join(" og ")}`;
};

export const BookClubCheckoutSummary = ({
  subscription,
  subscriptionImage,
}: BookClubCheckoutSummaryProps) => {
  const { control } = useFormContext<CheckoutForm>();
  const selectedGiftsList = useWatch({ control, name: "gifts" });
  const selectedGiftSections = useWatch({ control, name: "giftSections" });

  const selectedGifts = getSelectedGiftsFromForm({
    gifts: selectedGiftsList,
    giftSections: selectedGiftSections,
  });

  const orderPrice = resolveFirstOrderPrice(subscription) ?? 0;
  const freightPrice = subscription.freight.initial[0]?.priceVariant.price ?? 0;
  const totalPrice = orderPrice + freightPrice;
  const summaryContentLabel = getSummaryContentLabel(selectedGifts);
  const subscriptionBenefits =
    "benefits" in (subscription as Record<string, unknown>)
      ? ((subscription as { benefits?: string[] }).benefits ?? [])
      : [];

  return (
    <Card className="space-y-7 bg-[#F7F7F7] p-4 sm:p-7">
      <Typography as="h2" variant="title-lg" className="text-center">
        Din Bestilling
      </Typography>

      <Card className="overflow-hidden p-0">
        <div className="relative h-56 w-full sm:h-72">
          {subscriptionImage?.url ? (
            <Image
              src={subscriptionImage.url}
              alt={subscriptionImage.altText ?? subscription.externalName}
              fill
              className="object-cover"
            />
          ) : (
            <div className="h-full w-full bg-gray-200" />
          )}
        </div>
      </Card>

      <ul className="space-y-4">
        {subscriptionBenefits.map((benefit) => (
          <li key={benefit} className="flex items-start gap-3 text-base">
            <Check className="mt-0.5 h-6 w-6 shrink-0 text-[#2D3748]" />
            <span>{benefit}</span>
          </li>
        ))}
      </ul>

      <Card className="relative grid grid-cols-[1fr_auto] gap-2 p-4">
        <Typography as="h3" variant="title-md" className="col-span-full mb-0">
          Sammendrag
        </Typography>

        <ul className="col-span-full grid grid-cols-subgrid gap-2">
          <li className="col-span-full grid grid-cols-subgrid">
            <div>
              <span>{summaryContentLabel}</span>
              {selectedGifts.length > 0 ? (
                <ul className="mt-1 list-disc pl-5 text-sm text-gray-600">
                  {selectedGifts.map((gift) => (
                    <li key={gift.sku}>{gift.name}</li>
                  ))}
                </ul>
              ) : null}
            </div>
            <span
              className="justify-self-end text-right"
              data-testid="summary-price"
            >
              {formatPrice(orderPrice)}
            </span>
          </li>

          <li className="col-span-full grid grid-cols-subgrid">
            <span>{freightPrice === 0 ? "Gratis frakt" : "Frakt"}</span>
            <span
              className="justify-self-end text-right"
              data-testid="summary-price"
            >
              {formatPrice(freightPrice)}
            </span>
          </li>

          <Typography
            as="li"
            variant="title-md"
            className="col-span-full grid grid-cols-subgrid border-t pt-2"
          >
            <span>Å betale</span>
            <span
              className="justify-self-end text-right"
              data-testid="summary-price"
            >
              {formatPrice(totalPrice)}
            </span>
          </Typography>
        </ul>
      </Card>
    </Card>
  );
};
